import { Record } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { customParseFloat } from "common/helpers/Math";
import { geocoder } from "common/services/geocode.service";
import { handleErrors } from "common/reducers/Utils";
import { checkDate } from "common/helpers/Date";

export const addressFromJSON = (json: Partial<IAddressData>): AddressRecord => {
  json.lat = customParseFloat(json.lat as any);
  json.lon = customParseFloat(json.lon as any);

  if (json.created_at) {
    json.created_at = checkDate(json.created_at);
  }
  if (json.updated_at) {
    json.updated_at = checkDate(json.updated_at);
  }

  return new AddressRecord(json as any);
};

export const toJSON = (address: AddressRecord): IAddressData => {
  const addr = address.toJSON() as Partial<IAddressData>;

  if (addr.id !== undefined && addr.id <= 0) {
    delete addr.id;
  }

  return addr as IAddressData;
};

export const toOneLiner = (address: AddressRecord): string => {
  let oneLiner = `${address.line_1 || ""} ${address.line_2 || ""}`;
  oneLiner = `${oneLiner.trim()} ${address.city || ""}`;
  oneLiner = `${oneLiner.trim()}${address.state ? "," : ""} ${address.state || ""}`;
  oneLiner = `${oneLiner.trim()} ${address.postal_code || ""}`.trim();

  return oneLiner;
};

export const toCityStateZip = (address: AddressRecord): string => {
  let csz = `${address.city || ""}`;
  csz = `${csz.trim()}${address.state ? "," : ""} ${address.state || ""}`;
  csz = `${csz.trim()} ${address.postal_code || ""}`.trim();

  return csz;
};

export const stateRequired = (address: AddressRecord): boolean => {
  if (address.line_1 || address.line_2 || address.city || address.postal_code || address.country) {
    if (
      !address.line_1?.trim() ||
      !address.line_2?.trim() ||
      !address.city?.trim() ||
      !address.postal_code?.trim() ||
      !address.country?.trim()
    ) {
      return true;
    }
  }
  return false;
};

export const isValidLatLon = (address: AddressRecord) => {
  if (address.lat && address.lon) {
    return address.lat >= -90 && address.lat <= 90 && address.lon >= -180 && address.lon <= 180;
  }
  return false;
};

export const isValid = (address: AddressRecord) => {
  if (address.line_1 && address.city && address.state) {
    if (address.line_1.trim().length > 0 && address.city.trim().length > 0 && address.state.trim().length > 0) {
      return true;
    }
  }

  return false;
};

export const fullAddress = (address: AddressRecord) => {
  let addr = "";

  if (address.line_1) {
    addr = addr + " " + address.line_1;
  }
  if (address.line_2) {
    addr = addr + " " + address.line_2;
  }
  if (address.city) {
    addr = addr + " " + address.city;
  }
  if (address.state) {
    addr = addr + ", " + address.state;
  }
  if (address.postal_code) {
    addr = addr + " " + address.postal_code;
  }
  if (address.country) {
    addr = addr + " " + address.country;
  }

  return addr.trim();
};

export const zillowAddress = (address: AddressRecord) => {
  let addr = "";

  if (address.line_1) {
    addr = addr + "-" + address.line_1.split(" ").join("-");
    addr = addr.split(",").join("");
  }
  if (address.line_2) {
    addr = addr + "-" + address.line_2;
  }
  if (address.city) {
    addr = addr + "-" + address.city;
  }
  if (address.state) {
    addr = addr + "-" + address.state;
  }
  if (address.postal_code) {
    addr = addr + "-" + address.postal_code;
  }

  if (addr !== "") {
    addr = addr + "_rb";
  }

  return addr.trim().substr(1);
};

export const addressToEv = (addr: AddressRecord) => {
  return {
    address: `${addr.line_1} ${addr.line_2 ? addr.line_2.trim() : ""}`.trim(),
    city: addr.city,
    state: addr.state,
    zip: addr.postal_code,
    country: addr.country,
    latitude: addr.lat,
    longitude: addr.lon,
    address_type: 4,
  };
};

export const toLatLng = (address: AddressRecord): Nullable<{ latitude: number; longitude: number }> => {
  if (!address || !address.get("lat") || !address.get("lon")) {
    return null;
  }

  return {
    latitude: address.get("lat") || 0,
    longitude: address.get("lon") || 0,
  };
};

export const geoCoder = async (address: AddressRecord) => {
  try {
    const results = await geocoder({ address: fullAddress(address) });
    if (results) {
      const geoLocation = results[0].geometry.location;

      const area_2 = results[0].address_components.find((ac: any) => ac.types.includes("administrative_area_level_2"));
      const administrative_area_level_2 = area_2?.short_name || "";

      const area_3 = results[0].address_components.find((ac: any) => ac.types.includes("administrative_area_level_3"));
      const administrative_area_level_3 = area_3?.short_name || "";
      return address.merge({
        lat: Math.round(geoLocation.lat() * 1e6) / 1e6,
        lon: Math.round(geoLocation.lng() * 1e6) / 1e6,
        administrative_area_level_2,
        administrative_area_level_3,
      });
    }
  } catch (error) {
    handleErrors(error, false);
  }
};

export type AddressKind = "job" | "customer";

export interface IAddressData {
  id: number;
  kind?: AddressKind;
  description?: string;
  addressable_id?: number;
  addressable_type?: string;
  line_1?: string;
  line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  lat?: number;
  lon?: number;
  administrative_area_level_2?: string;
  administrative_area_level_3?: string;

  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IAddressRecord {
  id: Nullable<number>;
  kind: Nullable<AddressKind>;
  description: Nullable<string>;
  addressable_id: Nullable<number>;
  addressable_type: Nullable<string>;
  line_1: Nullable<string>;
  line_2: Nullable<string>;
  city: Nullable<string>;
  state: Nullable<string>;
  postal_code: Nullable<string>;
  country: Nullable<string>;
  lat: Nullable<number>;
  lon: Nullable<number>;
  administrative_area_level_2: Nullable<string>;
  administrative_area_level_3: Nullable<string>;
  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
}

export const defaultAddressProps = {
  id: 0,
  kind: null,
  description: null,
  addressable_id: null,
  addressable_type: null,
  line_1: null,
  line_2: null,
  city: null,
  state: null,
  postal_code: null,
  country: null,
  lat: null,
  lon: null,
  administrative_area_level_2: null,
  administrative_area_level_3: null,
  created_at: null,
  updated_at: null,
};

export class AddressRecord extends Record<IAddressRecord>(defaultAddressProps) implements IAddressRecord {}
